import { z } from 'zod';
/**
 * `art_director` (spec 90 §2, §5, REQ-90-018..020, S90-8): proposes a design
 * for one content variant from the registered templates - template per slide,
 * format, images from the candidate set, in-image texts per planned language,
 * alignment and logo. Mode `design` returns one proposal that the run applies
 * as a content version (`author_type = ai_job`); mode `variants` returns 2-3
 * proposals in distinct styles that stay in the run's output until a human
 * applies one (`POST /v1/designs/{id}/apply-proposal`).
 *
 * The output is a flat, provider-strict shape (no open records): module 90
 * maps it to template props and validates them against the template schema.
 */
export const ART_DIRECTOR_MODES = ['design', 'variants'];
/** Styles of "generate variants" (REQ-90-019); every proposal of a `variants` run has a different one. */
export const ART_DIRECTOR_STYLES = ['calm_editorial', 'image_strong', 'informative'];
/** Design formats (mirror of `TEMPLATE_FORMATS` in `packages/templates`, which this package does not depend on). */
export const ART_DIRECTOR_FORMATS = ['portrait', 'square', 'story'];
/** Proposal count per mode (AC-90-042, AC-90-044). */
export const ART_DIRECTOR_PROPOSAL_RANGE = {
    design: { min: 1, max: 1 },
    variants: { min: 2, max: 3 },
};
/** At most this many candidate photos go into one art-director context. */
export const ART_DIRECTOR_MAX_CANDIDATES = 40;
export const artDirectorInputSchema = z
    .object({
    content_item_id: z.uuid(),
    variant_id: z.uuid(),
    /** The item's version the proposal is based on; a later change fails a `design` run `content_changed`. */
    base_version_id: z.uuid(),
    mode: z.enum(ART_DIRECTOR_MODES),
})
    .strict();
const imageChoiceSchema = z
    .object({
    slot: z.string().min(1).max(40),
    asset_id: z.string().min(1).max(64),
})
    .strict();
const textChoiceSchema = z
    .object({
    language: z.string().min(2).max(35),
    field: z.string().min(1).max(40),
    text: z.string().min(1).max(400),
})
    .strict();
export const artDirectorSlideSchema = z
    .object({
    /** A `template` of the context's catalog (not `template_key`: the run log drops `*key*` fields as credential-like). */
    template: z.string().min(1).max(64),
    images: z.array(imageChoiceSchema).max(4),
    texts: z.array(textChoiceSchema).max(60),
    text_align: z.enum(['start', 'center']),
    show_logo: z.boolean(),
})
    .strict();
export const artDirectorProposalSchema = z
    .object({
    style: z.enum(ART_DIRECTOR_STYLES),
    format: z.enum(ART_DIRECTOR_FORMATS),
    /** One slide for a single-image variant, 2-10 in order for a carousel. */
    slides: z.array(artDirectorSlideSchema).min(1).max(10),
    rationale: z.string().min(1).max(400),
})
    .strict();
export const artDirectorOutputSchema = z
    .object({
    proposals: z.array(artDirectorProposalSchema).min(1).max(3),
})
    .strict();
/** How the art director designs a property's posts (spec 90 section 4.8, REQ-90-027). */
export const DESIGN_PREFERENCES = ['photo_first', 'balanced', 'designed'];
export const DEFAULT_DESIGN_PREFERENCE = 'balanced';
/** At most this many favoured templates per property. */
export const MAX_PREFERRED_TEMPLATES = 6;
//# sourceMappingURL=art-director.js.map